import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Divider, Grid, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FormSelect, FormTextField } from "@zfp-akademie-a-s/zfp-design/form";
import { FormNumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import { type PensionFormType, getPensionFormSchema } from "Forms/Schemas/GoalsFulfillment/CurrentSolution/pensionFormSchema";
import { type Option, toSelectOptions } from "Forms/fieldOptions";
import { useGetAnalysisClients } from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  getGetAssetAllocationOverviewQueryKey,
  getListCurrentContractsQueryKey,
  useCreateCurrentContract,
  usePatchCurrentContract,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import {
  AmountFrequency,
  CreateCurrentContractPensionSavingBodyInvestmentToolType,
  type CreateCurrentContractPensionSavingBodyProposedStepActionType,
  type CreateCurrentContractPensionSavingBodySolutionHorizonType,
  type CurrentContractBaseInvestmentToolType,
  type CurrentContractBaseSolutionHorizonType,
  type CurrentContractDetailPensionSaving,
  CurrentContractListItemProposedStepActionType,
  type CurrentSolutionAssetOption,
  PatchCurrentContractPensionSavingBodyInvestmentToolType,
  type PatchCurrentContractPensionSavingBodyProposedStepActionType,
  PensionSavingDetailFundType,
  PensionSavingDetailPensionSavingStrategy,
} from "Infrastructure/ApiZfp/Schema";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { format } from "date-fns";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useGoalsFulfillmentContext } from "../../GoalsFulfillmentContext";
import { CharacteristicsAccordion } from "../CharacteristicsAccordion";
import type { CurrentSolutionFormHandle } from "./CurrentSolutionWrapperForm";
import { ModifyDepositFields } from "./ModifyDepositFields";

type Props = {
  readonly edit?: boolean;
  readonly investmentToolType: CurrentContractBaseInvestmentToolType;
  readonly institution?: string;
  readonly horizonType: CurrentContractBaseSolutionHorizonType;
  readonly onSuccess?: () => void;
  readonly currentSolutionId?: string;
  readonly initialData?: CurrentContractDetailPensionSaving | null;
};

export const PensionForm = forwardRef<CurrentSolutionFormHandle, Props>(
  ({ investmentToolType, institution, edit, horizonType, onSuccess, currentSolutionId, initialData }, ref) => {
    const { t } = useResource();
    const queryClient = useQueryClient();
    const institutionRef = useRef(institution);
    const { analysisId, versionId } = useContext(AnalysisDetailContext);
    const { assetAllocationOverview } = useGoalsFulfillmentContext();
    const { data: clientsData } = useGetAnalysisClients(analysisId, versionId);

    const { mutate: createMutation } = useCreateCurrentContract();
    const { mutate: updateMutation } = usePatchCurrentContract();

    const clientOptions: Option[] = (clientsData?.clients ?? []).map((client) => ({
      value: client.guid,
      name: `${client.firstName} ${client.surname} (${client.birthDate ? format(new Date(client.birthDate), "dd.MM.yyyy") : ""})`,
    }));

    const proposedStepActionOptions: Option[] = (
      [
        CurrentContractListItemProposedStepActionType.Keep,
        CurrentContractListItemProposedStepActionType.Modify,
        CurrentContractListItemProposedStepActionType.Cancel,
      ] as Array<keyof typeof CurrentContractListItemProposedStepActionType>
    ).map((value) => ({
      value,
      name: t(Resources.AnalysisDetail.GoalsFulfillment.ProposedStepActionTypeOptions[value]),
    }));

    const fundOptions: Option[] = (Object.keys(PensionSavingDetailFundType) as Array<keyof typeof PensionSavingDetailFundType>).map(
      (value) => ({
        value,
        name: t(Resources.AnalysisDetail.GoalsFulfillment.FundOptions[value]),
      }),
    );

    const strategyOptions: Option[] = (
      Object.keys(PensionSavingDetailPensionSavingStrategy) as Array<keyof typeof PensionSavingDetailPensionSavingStrategy>
    ).map((value) => ({
      value,
      name: t(Resources.AnalysisDetail.GoalsFulfillment.StrategyOptions[value]),
    }));

    const methods = useForm<PensionFormType>({
      resolver: yupResolver(getPensionFormSchema(t, assetAllocationOverview?.isPortfolioAssetAllocationRequired)),
      mode: "onChange",
      defaultValues:
        edit && initialData
          ? {
              investmentToolType: initialData.investmentToolType as any,
              institution: initialData.partnerName ?? "",
              clientId: initialData.client?.guid ?? "",
              fund: initialData.pensionSavingDetail?.fundType ?? "",
              strategy: initialData.pensionSavingDetail?.pensionSavingStrategy ?? "",
              currentValue: initialData.oneTimeAmount?.value ?? 0,
              regularAmount: initialData.regularAmount?.value ?? 0,
              horizon: initialData.currentSolutionHorizon ?? 0,
              appreciationRate: initialData.appreciationRate ?? 0,
              employerDeposit: initialData.pensionSavingDetail?.employerContribution?.value ?? 0,
              stateContribution: initialData.pensionSavingDetail?.stateContribution?.value ?? 0,
              managementFee: initialData.managementFee ?? 0,
              equities: initialData.assetAllocation?.equities ?? 0,
              bonds: initialData.assetAllocation?.bonds ?? 0,
              commodities: initialData.assetAllocation?.commodities ?? 0,
              realEstate: initialData.assetAllocation?.realEstate ?? 0,
              cash: initialData.assetAllocation?.cash ?? 0,
              other: initialData.assetAllocation?.others ?? 0,
              proposedStepAction: initialData.proposedStepActionType,
              newDeposit: (initialData.oneTimeAmount?.value ?? 0) + (initialData.oneTimeAmountChange?.value ?? 0),
              newRegularDeposit: (initialData.regularAmount?.value ?? 0) + (initialData.regularAmountChange?.value ?? 0),
              note: initialData.note ?? "",
            }
          : {
              investmentToolType,
              institution,
              clientId: undefined,
            },
    });

    const { control, watch, handleSubmit, reset, setValue, getValues } = methods;

    const proposedStepAction = watch("proposedStepAction");

    useEffect(() => {
      if (edit && initialData) {
        reset({
          investmentToolType: initialData.investmentToolType as any,
          institution: institutionRef.current,
          clientId: initialData.client?.guid ?? "",
          fund: initialData.pensionSavingDetail?.fundType ?? "",
          strategy: initialData.pensionSavingDetail?.pensionSavingStrategy ?? "",
          currentValue: initialData.oneTimeAmount?.value ?? 0,
          regularAmount: initialData.regularAmount?.value ?? 0,
          horizon: initialData.currentSolutionHorizon ?? 0,
          appreciationRate: initialData.appreciationRate ?? 0,
          employerDeposit: initialData.pensionSavingDetail?.employerContribution?.value ?? 0,
          stateContribution: initialData.pensionSavingDetail?.stateContribution?.value ?? 0,
          managementFee: initialData.managementFee ?? 0,
          equities: initialData.assetAllocation?.equities ?? 0,
          bonds: initialData.assetAllocation?.bonds ?? 0,
          commodities: initialData.assetAllocation?.commodities ?? 0,
          realEstate: initialData.assetAllocation?.realEstate ?? 0,
          cash: initialData.assetAllocation?.cash ?? 0,
          other: initialData.assetAllocation?.others ?? 0,
          proposedStepAction: initialData.proposedStepActionType,
          newDeposit: (initialData.oneTimeAmount?.value ?? 0) + (initialData.oneTimeAmountChange?.value ?? 0),
          newRegularDeposit: (initialData.regularAmount?.value ?? 0) + (initialData.regularAmountChange?.value ?? 0),
          note: initialData.note ?? "",
        });
      }
    }, [edit, initialData, reset]);

    useEffect(() => {
      setValue("institution", institution ?? "", { shouldValidate: true });
    }, [institution, setValue]);

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (edit) {
          handleSubmit(onEdit)();
        } else {
          handleSubmit(onCreate)();
        }
      },
      prefillFromAssetOption: (asset: CurrentSolutionAssetOption) => {
        setValue("investmentToolType", asset.investmentToolType as any, { shouldValidate: true, shouldDirty: true });
        // TODO: set Client GUID when Assets will be changed from BL BE to ZFP BE
        // setValue("clientId", asset.clientID ?? "", { shouldValidate: true, shouldDirty: true });
        setValue("currentValue", asset.amount?.value ?? 0, { shouldValidate: true, shouldDirty: true });
        setValue("regularAmount", asset.regularAmount?.value ?? 0, { shouldValidate: true, shouldDirty: true });
      },
    }));

    const onCreate = (data: PensionFormType) => {
      const isProposedStepModify = data.proposedStepAction === CurrentContractListItemProposedStepActionType.Modify;
      createMutation(
        {
          analysisId,
          versionId,
          data: {
            investmentToolType: CreateCurrentContractPensionSavingBodyInvestmentToolType.pension_saving,
            solutionHorizonType: horizonType as CreateCurrentContractPensionSavingBodySolutionHorizonType,
            clientGuid: data.clientId,
            proposedStepActionType: data.proposedStepAction as CreateCurrentContractPensionSavingBodyProposedStepActionType,
            partnerName: institution ?? "",
            oneTimeAmount: {
              value: data.currentValue,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.one_time,
            },
            regularAmount: {
              value: data.regularAmount,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.monthly,
            },
            oneTimeAmountChange: isProposedStepModify
              ? {
                  value: (data.newDeposit ?? 0) - data.currentValue,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.one_time,
                }
              : undefined,
            regularAmountChange: isProposedStepModify
              ? {
                  value: (data.newRegularDeposit ?? 0) - data.regularAmount,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.monthly,
                }
              : undefined,
            assetAllocation: {
              equities: data.equities ?? 0,
              bonds: data.bonds ?? 0,
              commodities: data.commodities ?? 0,
              realEstate: data.realEstate ?? 0,
              cash: data.cash ?? 0,
              others: data.other ?? 0,
            },
            currentSolutionHorizon: data.horizon ?? 0,
            appreciationRate: data.appreciationRate,
            note: data.note ?? "",
            managementFee: data.managementFee ?? 0,
            pensionSavingDetail: {
              pensionSavingStrategy: data.strategy as any,
              fundType: data.fund as any,
              employerContribution: {
                value: data.employerDeposit ?? 0,
                currencyCode: CurrencyCode.CZK,
                frequency: AmountFrequency.monthly,
              },
              stateContribution: {
                value: data.stateContribution ?? 0,
                currencyCode: CurrencyCode.CZK,
                frequency: AmountFrequency.monthly,
              },
            },
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListCurrentContractsQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
            onSuccess?.();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
          },
        },
      );
    };

    const onEdit = (data: PensionFormType) => {
      if (!currentSolutionId) return;
      const isProposedStepModify = data.proposedStepAction === CurrentContractListItemProposedStepActionType.Modify;
      updateMutation(
        {
          analysisId,
          versionId,
          currentContractGuid: currentSolutionId,
          data: {
            investmentToolType: PatchCurrentContractPensionSavingBodyInvestmentToolType.pension_saving,
            solutionHorizonType: horizonType,
            partnerName: institution ?? "",
            clientGuid: data.clientId,
            proposedStepActionType: data.proposedStepAction as PatchCurrentContractPensionSavingBodyProposedStepActionType,
            oneTimeAmount: {
              value: data.currentValue,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.one_time,
            },
            regularAmount: {
              value: data.regularAmount,
              currencyCode: CurrencyCode.CZK,
              frequency: AmountFrequency.monthly,
            },
            oneTimeAmountChange: isProposedStepModify
              ? {
                  value: (data.newDeposit ?? 0) - data.currentValue,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.one_time,
                }
              : undefined,
            regularAmountChange: isProposedStepModify
              ? {
                  value: (data.newRegularDeposit ?? 0) - data.regularAmount,
                  currencyCode: CurrencyCode.CZK,
                  frequency: AmountFrequency.monthly,
                }
              : undefined,
            assetAllocation: {
              equities: data.equities ?? 0,
              bonds: data.bonds ?? 0,
              commodities: data.commodities ?? 0,
              realEstate: data.realEstate ?? 0,
              cash: data.cash ?? 0,
              others: data.other ?? 0,
            },
            currentSolutionHorizon: data.horizon ?? 0,
            appreciationRate: data.appreciationRate,
            note: data.note ?? "",
            managementFee: data.managementFee ?? 0,
            pensionSavingDetail: {
              pensionSavingStrategy: data.strategy as any,
              fundType: data.fund as any,
              employerContribution: {
                value: data.employerDeposit ?? 0,
                currencyCode: CurrencyCode.CZK,
                frequency: AmountFrequency.monthly,
              },
              stateContribution: {
                value: data.stateContribution ?? 0,
                currencyCode: CurrencyCode.CZK,
                frequency: AmountFrequency.monthly,
              },
            },
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListCurrentContractsQueryKey(analysisId, versionId) });
            queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
            onSuccess?.();
          },
          onError: () => {
            toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
          },
        },
      );
    };

    const handleProposedStepActionChange = (value: string) => {
      if (value === CurrentContractListItemProposedStepActionType.Modify) {
        const currentForm = getValues();
        const initializationMap: Record<string, any> = {
          newDeposit: currentForm.currentValue,
          newRegularDeposit: currentForm.regularAmount,
        };

        for (const [field, sourceValue] of Object.entries(initializationMap)) {
          const currentValue = getValues(field as any);
          if (currentValue === undefined || currentValue === null || currentValue === "" || currentValue === 0) {
            setValue(field as any, sourceValue, {
              shouldDirty: true,
            });
          }
        }
      }
    };

    return (
      <FormProvider {...methods}>
        <form>
          <Stack spacing={Spacing.XXL}>
            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <FormSelect
                    control={control}
                    name="clientId"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ClientId)}
                    required
                    options={toSelectOptions(clientOptions)}
                    placeholder={t(Resources.Common.SelectPlaceholder)}
                    fullWidth
                  />
                </Grid>
                <Grid size={4}>
                  <FormSelect
                    control={control}
                    name="fund"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FundOptions.Title)}
                    required
                    options={toSelectOptions(fundOptions)}
                    placeholder={t(Resources.Common.SelectPlaceholder)}
                    fullWidth
                  />
                </Grid>
                <Grid size={4}>
                  <FormNumberField
                    control={control}
                    name="currentValue"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.CurrentValue)}
                    required
                    scale={2}
                    thousandsSeparator=" "
                    unit={t(Resources.Common.CZKSuffix)}
                    fullWidth
                  />
                </Grid>
                <Grid size={4}>
                  <FormNumberField
                    control={control}
                    name="horizon"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Horizon)}
                    required
                    scale={0}
                    thousandsSeparator=" "
                    unit={t(Resources.Common.Years)}
                    fullWidth
                  />
                </Grid>
                <Grid size={4}>
                  <FormNumberField
                    control={control}
                    name="regularAmount"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.RegularAmount)}
                    required
                    scale={2}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    fullWidth
                  />
                </Grid>
                <Grid size={4}>
                  <FormNumberField
                    control={control}
                    name="employerDeposit"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.EmployerDeposit)}
                    scale={2}
                    thousandsSeparator=" "
                    unit={t(Resources.Common.CZKSuffix)}
                    fullWidth
                  />
                </Grid>
                <Grid size={4}>
                  <FormNumberField
                    control={control}
                    name="appreciationRate"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.AppreciationRate)}
                    required
                    scale={2}
                    thousandsSeparator=" "
                    unit="%"
                    fullWidth
                  />
                </Grid>

                <Grid size={4}>
                  <FormSelect
                    control={control}
                    name="strategy"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.StrategyOptions.Title)}
                    required
                    options={toSelectOptions(strategyOptions)}
                    placeholder={t(Resources.Common.SelectPlaceholder)}
                    fullWidth
                  />
                </Grid>
                <Grid size={4}>
                  <FormNumberField
                    control={control}
                    name="stateContribution"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.StateContribution)}
                    scale={2}
                    thousandsSeparator=" "
                    unit={`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}
                    fullWidth
                  />
                </Grid>
                <Grid size={4}>
                  <FormNumberField
                    control={control}
                    name="managementFee"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.ManagementFee)}
                    scale={2}
                    thousandsSeparator=" "
                    unit="%"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <CharacteristicsAccordion<PensionFormType>
                key={assetAllocationOverview?.isPortfolioAssetAllocationRequired ? "open" : "closed"}
                defaultExpanded={assetAllocationOverview?.isPortfolioAssetAllocationRequired}
              />
            </Box>

            <Divider />

            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t(Resources.AnalysisDetail.GoalsFulfillment.ProposedSolutionSteps)}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <FormSelect
                    control={control}
                    name="proposedStepAction"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.ProposedStepActionTypeOptions.Title)}
                    required
                    options={toSelectOptions(proposedStepActionOptions)}
                    placeholder={t(Resources.Common.SelectPlaceholder)}
                    onChange={handleProposedStepActionChange}
                    fullWidth
                  />
                </Grid>

                {proposedStepAction === CurrentContractListItemProposedStepActionType.Modify && <ModifyDepositFields />}
              </Grid>
            </Box>

            <Divider />

            <Box>
              <Grid container spacing={Spacing.XL}>
                <Grid size={12}>
                  <FormTextField
                    control={control}
                    name="note"
                    label={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Note)}
                    maxLength={NOTE_MAX_LENGTH}
                    placeholder={t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.NotePlaceholder)}
                    multiline
                    minRows={6}
                    maxRows={8}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </form>
      </FormProvider>
    );
  },
);
